import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Building2, ChevronRight, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth/auth-provider";
import { fetchTenantMemberships } from "@/lib/api/tenants";
import { BrandLockup, EmptyState, Spinner } from "@/components/ui/primitives";
import { initials } from "@/lib/format";

export function SelectTenantPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const {
    data: memberships,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["tenant-memberships", user?.id],
    queryFn: fetchTenantMemberships,
    enabled: !!user,
  });

  return (
    <main className="argus-glow bg-neutral-975 flex min-h-dvh flex-col items-center justify-center px-6 text-neutral-100">
      <div className="w-full max-w-md space-y-8">
        <header className="flex flex-col items-center gap-4 text-center">
          <BrandLockup size="md" />
          <div className="space-y-1">
            <h1 className="text-lg font-semibold tracking-tight text-neutral-100">
              Choose a workspace
            </h1>
            <p className="text-sm text-neutral-400">
              Signed in as <span className="text-neutral-200">{user?.email}</span>
            </p>
          </div>
        </header>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-neutral-500">
            <Spinner /> Loading workspaces…
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
            {error.message}
          </div>
        ) : memberships && memberships.length > 0 ? (
          <ul className="space-y-2">
            {memberships.map((m) => (
              <li key={m.tenant_id}>
                <Link
                  to="/t/$slug/dashboard"
                  params={{ slug: m.tenant.slug }}
                  className="group flex items-center gap-3 rounded-xl border border-neutral-800/80 bg-neutral-900/50 px-4 py-3 shadow-sm shadow-black/20 transition hover:border-neutral-700 hover:bg-neutral-900"
                >
                  <div className="bg-brand-950/60 text-brand-300 flex size-9 shrink-0 items-center justify-center rounded-lg text-xs font-semibold">
                    {initials(m.tenant.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-neutral-100">
                      {m.tenant.name}
                    </div>
                    <div className="truncate text-[11px] capitalize text-neutral-500">
                      {m.role} · {m.tenant.plan.replace(/_/g, " ")} plan
                    </div>
                  </div>
                  <ChevronRight className="size-4 text-neutral-600 transition group-hover:translate-x-0.5 group-hover:text-neutral-400" />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<Building2 className="size-6" />}
            title="No workspaces yet"
            description="You're signed in but not a member of any workspace yet. Ask your firm's Argus owner to invite you."
          />
        )}

        <div className="text-center">
          <button
            onClick={() => {
              void (async () => {
                await signOut();
                await navigate({ to: "/sign-in", replace: true });
              })();
            }}
            className="inline-flex items-center gap-1.5 text-xs text-neutral-500 transition hover:text-neutral-300"
          >
            <LogOut className="size-3" />
            Sign out
          </button>
        </div>
      </div>
    </main>
  );
}
